import {
  getCurrentInstance,
  Ref,
  ref,
  watch,
  computed,
  toRaw
} from '@vue/composition-api';

type InnerVal<T> = T;

// Composables
export function useProxiedModel<
  Props extends Record<string, any>,
  Prop extends Extract<keyof Props, string>,
  Inner = Props[Prop]
>(
  props: Props,
  prop: Prop,
  defaultValue?: Props[Prop],
  transformIn: (value: Props[Prop]) => Inner = (v: any) => v,
  transformOut: (value: Inner) => Props[Prop] = (v: any) => v
) {
  const vm = getCurrentInstance();
  const internal = ref(
    props[prop] !== undefined ? props[prop] : defaultValue
  ) as Ref<Props[Prop]>;

  watch(
    () => props[prop],
    val => {
      internal.value = val;
    }
  );

  const model = computed({
    get(): any {
      return transformIn(internal.value);
    },
    set(internalValue) {
      const newValue = transformOut(internalValue);
      const value = toRaw(internal.value);
      if (value === newValue || transformIn(value) === internalValue) {
        return;
      }
      internal.value = newValue;
      vm?.emit(`input`, newValue);
    }
  }) as Ref<InnerVal<Inner>>;

  return model;
}
